import { createFeatureSelector, createSelector, select } from '@ngrx/store'
import { StoreToken } from '@core/store/enums/store-tokens.enum'
import { pipe } from 'rxjs'
import { distinctUntilChanged, map } from 'rxjs/operators'

import { Topics } from '@sockets/models/socket.model'
import { Topic } from '@sockets/enums/socket.enum'
import { mapMatch, sortByMatchdayDescDateDesc } from '@core/utils/match.utils'
import { deepEqual, exists } from '@core/utils/object.utils'
import { SocketState } from '@core/store/states/socket.state'
import { selectSimplifiedLang } from '@core/store/selectors/user.selectors'
import { APP_VERSION } from '@core/constants/application.constants'
import { Capacitor } from '@capacitor/core'
import { PlatformType } from '@core/enums/application.enums'
import { Match } from '@core/models/match.models'
import { selectCurrentSeasonByContext } from '@features/seasons/state/selectors/seasons.selectors'
import { CallingContext } from '@features/seasons/models/seasons.models'

export const selectSocket = createFeatureSelector<SocketState>(StoreToken.socket)

/**
 * Gets all stored topics as a Map
 *
 * @see Topics
 * @return {Observable<Topics>}
 */
export const selectTopics = createSelector(selectSocket, ({ topics }): Topics => topics)

/**
 * Get a topic value or a set of topics as a Map
 * If a single topic is received, it will return its content. Otherwise, it will return a subset of all topics as a Map
 *
 * @param topics
 * @see Topics
 * @return
 */
// export const getTopic = createSelector(
//   selectSocket, (state, topics: Topic | Topic[]): Topics | unknown => Array.isArray(topics) ?
//     topics.reduce((obj, topic) => ({
//       ...obj,
//       [topic]: exists(state.topics) ? state.topics[topic] : null
//     }), {})
//     : exists(state.topics) ? state.topics[topics] : null
// )

export const selectTopic = (topics: Topic | Topic[]) =>
  createSelector(selectSocket, (state) =>
    Array.isArray(topics)
      ? topics.reduce(
          (obj, topic) => ({
            ...obj,
            [topic]: exists(state.topics) ? state.topics[topic] : null
          }),
          {}
        )
      : exists(state.topics)
        ? state.topics[topics]
        : null
  )

export const selectDistinctTopic = (topics: Topic | Topic[]) =>
  pipe(select(selectTopic(topics)), distinctUntilChanged(deepEqual))

export const selectSocketStatus = createSelector(selectSocket, (state) => state.status)

export const selectShouldUpdateVersion = pipe(
  select(selectTopics),
  distinctUntilChanged(deepEqual),
  map(
    (topics) =>
      (topics[Topic.ForceUpdateVersion] || !topics[Topic.IsLive]) &&
      APP_VERSION < (topics[Topic.BuildVersion] as number) &&
      Capacitor.getPlatform() === PlatformType.Android
  )
)

export const selectLiveMatches = (callingContext: CallingContext) =>
  createSelector(
    selectTopic(Topic.LiveMatches),
    selectSimplifiedLang,
    selectCurrentSeasonByContext(callingContext),
    (liveMatches: Match[], language, currentSeason) => {
      if (!!liveMatches && !!language && !!currentSeason) {
        return sortByMatchdayDescDateDesc(liveMatches.map((match) => mapMatch(match)))
      }
    }
  )
